import React, { useState, useEffect } from 'react'
import {
  ShieldCheckIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  CheckIcon,
  XMarkIcon,
  CalendarIcon,
  UserIcon,
  ArrowPathIcon,
  ForwardIcon
} from '@heroicons/react/24/outline'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import api from '../../services/api'

interface KYCRecord {
  id: number
  full_name: string
  email: string
  username: string
  phone?: string
  is_active: boolean
  kyc_status: string
  kyc: {
    status: string
    esign_status: string
    cams_kyc_verified: string | null
    submitted_at?: string
  } | null
}

const KYCManagement: React.FC = () => {
  const [records, setRecords] = useState<KYCRecord[]>([])
  const [filteredRecords, setFilteredRecords] = useState<KYCRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedRecord, setSelectedRecord] = useState<KYCRecord | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [remarks, setRemarks] = useState('')

  useEffect(() => {
    fetchKYCRecords()
  }, [])

  useEffect(() => {
    let filtered = records
    if (searchTerm) {
      filtered = filtered.filter(r =>
        r.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.username.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.kyc_status === statusFilter)
    }
    setFilteredRecords(filtered)
  }, [records, searchTerm, statusFilter])

  const fetchKYCRecords = async () => {
    try {
      setLoading(true)
      const res = await api.get('/admin/clients')
      const clients: KYCRecord[] = res.data.clients || []
      setRecords(clients)
    } catch (error) {
      console.error('Error fetching KYC records:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleKYCAction = async (userId: number, action: 'approve' | 'reject' | 'skip') => {
    if (action === 'skip' && !window.confirm('Skip KYC for this user? They will be allowed to continue without KYC verification.')) {
      return
    }
    try {
      setActionLoading(true)
      await api.put(`/admin/clients/${userId}/kyc`, { action, remarks })
      await fetchKYCRecords()
      setShowModal(false)
      setRemarks('')
    } catch (error) {
      console.error('Error updating KYC:', error)
    } finally {
      setActionLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-500/10 text-green-400'
      case 'rejected': return 'bg-red-500/10 text-red-400'
      case 'pending':
      case 'under_review': return 'bg-yellow-500/10 text-yellow-400'
      default: return 'bg-dark-700 text-gray-400'
    }
  }

  const getKYCStatusLabel = (status: string) => {
    if (status === 'approved') return 'Approved'
    if (status === 'under_review') return 'Under Review'
    if (status === 'pending') return 'Pending'
    if (status === 'rejected') return 'Rejected'
    return 'Not Started'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">KYC Management</h1>
          <p className="text-gray-400 mt-1">Review, approve, or skip user KYC verification</p>
        </div>
        <Button variant="outline" onClick={fetchKYCRecords}>
          <ArrowPathIcon className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{records.length}</p>
            <p className="text-sm text-gray-400">Total Clients</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-400">{records.filter(r => r.kyc_status === 'pending' || r.kyc_status === 'under_review').length}</p>
            <p className="text-sm text-gray-400">Pending KYC</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-400">{records.filter(r => r.kyc_status === 'approved').length}</p>
            <p className="text-sm text-gray-400">Approved</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-400">{records.filter(r => r.kyc_status === 'rejected').length}</p>
            <p className="text-sm text-gray-400">Rejected</p>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-dark-900 border border-dark-600 text-white rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="under_review">Under Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </Card>

      {/* Table */}
      <Card padding={false}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-dark-900 border-b border-dark-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">KYC Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">eSign</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">CAMS Verified</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Submitted</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {filteredRecords.map((record) => (
                <tr key={record.id} className="hover:bg-dark-700">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-9 h-9 bg-blue-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <UserIcon className="w-5 h-5 text-blue-400" />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-white">{record.full_name}</div>
                        <div className="text-xs text-gray-400">{record.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(record.kyc_status)}`}>
                      {getKYCStatusLabel(record.kyc_status)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(record.kyc?.esign_status || 'pending')}`}>
                      {(record.kyc?.esign_status || 'not started').replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {record.kyc?.cams_kyc_verified === 'yes' ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-400">
                        <CheckIcon className="w-3.5 h-3.5" /> Verified
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500">Not verified</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-400">
                    {record.kyc?.submitted_at ? (
                      <div className="flex items-center">
                        <CalendarIcon className="w-4 h-4 mr-1" />
                        {new Date(record.kyc.submitted_at).toLocaleDateString('en-IN')}
                      </div>
                    ) : '—'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <Button size="sm" variant="ghost" onClick={() => { setSelectedRecord(record); setRemarks(''); setShowModal(true) }}>
                        <EyeIcon className="w-4 h-4" />
                      </Button>
                      {record.kyc && (record.kyc_status === 'pending' || record.kyc_status === 'under_review') && (
                        <>
                          <Button size="sm" variant="ghost" title="Approve KYC" onClick={() => handleKYCAction(record.id, 'approve')}>
                            <CheckIcon className="w-4 h-4 text-green-400" />
                          </Button>
                          <Button size="sm" variant="ghost" title="Reject KYC" onClick={() => handleKYCAction(record.id, 'reject')}>
                            <XMarkIcon className="w-4 h-4 text-red-400" />
                          </Button>
                        </>
                      )}
                      {record.kyc_status !== 'approved' && (
                        <Button size="sm" variant="ghost" title="Skip KYC" onClick={() => handleKYCAction(record.id, 'skip')}>
                          <ForwardIcon className="w-4 h-4 text-amber-400" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredRecords.length === 0 && (
            <div className="text-center py-12">
              <ShieldCheckIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400">No clients found</p>
            </div>
          )}
        </div>
      </Card>

      {/* Detail Modal */}
      {showModal && selectedRecord && (
        <div className="fixed inset-0 bg-black/60 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-dark-700 w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-xl bg-dark-800">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-bold text-white">KYC Details</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-200">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 mb-0.5">Name</p>
                  <p className="text-white">{selectedRecord.full_name}</p>
                </div>
                <div>
                  <p className="text-gray-400 mb-0.5">Email</p>
                  <p className="text-white">{selectedRecord.email}</p>
                </div>
                <div>
                  <p className="text-gray-400 mb-0.5">KYC Status</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedRecord.kyc_status)}`}>
                    {getKYCStatusLabel(selectedRecord.kyc_status)}
                  </span>
                </div>
                <div>
                  <p className="text-gray-400 mb-0.5">eSign Status</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedRecord.kyc?.esign_status || 'pending')}`}>
                    {(selectedRecord.kyc?.esign_status || 'not started').replace('_', ' ')}
                  </span>
                </div>
                <div>
                  <p className="text-gray-400 mb-0.5">CAMS Verified</p>
                  <p className={selectedRecord.kyc?.cams_kyc_verified === 'yes' ? 'text-green-400' : 'text-gray-400'}>
                    {selectedRecord.kyc?.cams_kyc_verified === 'yes' ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 mb-0.5">Submitted</p>
                  <p className="text-white">
                    {selectedRecord.kyc?.submitted_at ? new Date(selectedRecord.kyc.submitted_at).toLocaleString('en-IN') : '—'}
                  </p>
                </div>
              </div>

              {selectedRecord.kyc_status !== 'approved' && (
                <div>
                  <p className="text-gray-400 mb-1">Remarks (optional)</p>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 bg-dark-900 border border-dark-600 text-white rounded-lg resize-none focus:ring-1 focus:ring-primary-500"
                    placeholder="Add admin remarks..."
                  />
                  {selectedRecord.kyc && (selectedRecord.kyc_status === 'pending' || selectedRecord.kyc_status === 'under_review') && (
                    <div className="flex gap-3 mt-3">
                      <Button
                        className="flex-1"
                        onClick={() => handleKYCAction(selectedRecord.id, 'approve')}
                        disabled={actionLoading}
                      >
                        Approve KYC
                      </Button>
                      <Button
                        variant="danger"
                        className="flex-1"
                        onClick={() => handleKYCAction(selectedRecord.id, 'reject')}
                        disabled={actionLoading}
                      >
                        Reject KYC
                      </Button>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    className="w-full mt-3"
                    onClick={() => handleKYCAction(selectedRecord.id, 'skip')}
                    disabled={actionLoading}
                  >
                    <ForwardIcon className="w-4 h-4 mr-2" />
                    Skip KYC — Allow user to continue without verification
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default KYCManagement
